import { RowConstructor, UrlUtil } from ".";
import { Api } from "./Api";
import { ArrayUtil } from "./ArrayUtil";
import { Collection } from "./Collection";
import { ErrorHandler } from "./ErrorHandler";
import { ModelRow, ModelRowType } from "./ModelRow";
import { ModelSearchFilter } from "./ModelSearchFilter";
import { OrderByInfo } from "./OrderByInfo";

export const Model = {
    searchSingleRecord: searchSingleRecordModel,
    search: searchModel,
    getDefaultRow: getDefaultModelRow
}

async function getDefaultModelRow<RowType>(modelPath: string, fieldList?: string, defaultSearchData?: boolean): Promise<RowType> {
    const params = {} as any;

    if (fieldList != null) {
        params._field_list = fieldList;
    }

    if (defaultSearchData === true) {
        params._default_search_data = true;
        params._search_url_props = UrlUtil.getPropsFromUrl(window.location.search);
    }

    const queryString = UrlUtil.buildQueryString(params);
    const resolvedPath = `${modelPath}${queryString}`;

    const result = await Api.get(resolvedPath);
    return result.data[0];
}

async function searchSingleRecordModel<RowType>(modelPath: string, filter?: any, fieldList?: string | object, errorHandler?: ErrorHandler, rowConstructor?: RowConstructor): Promise<ModelRow> {
    const response = await searchModel<RowType>(modelPath, filter, fieldList, errorHandler, rowConstructor, 0);
    if (response.modelRows.length === 0)
        return null;
    else
        return response.getSingleModelRow();
}

async function searchModel<RowType>(modelPath: string, filter?: any, fieldList?: string | object, errorHandler?: ErrorHandler, rowConstructor?: RowConstructor, index?: number): Promise<ModelSearchResult> {
    const searchFilter = ModelSearchFilter.from(filter);
    if (fieldList !== undefined) {
        searchFilter.fieldList = fieldList;
    }
    return Api.search<ModelSearchResult>(modelPath, searchFilter.getSearchBody(), null, errorHandler).then(response => {
        const result: ModelSearchResult = new ModelSearchResult();

        if (index != null && response?.data.length > 0)
            result.modelRows.push(buildModelRow(response.data[index], modelPath, rowConstructor, searchFilter));
        else
            for (const data of response.data)
                result.modelRows.push(buildModelRow(data, modelPath, rowConstructor, searchFilter))

        result.orderBy = _fillOrderBy(response.order_by);

        if (response.actual_row_count != null)
            result.actualRowCount = response.actual_row_count;

        if (response.summary_data != null)
            result.summaryData = new ModelRow<any>("", false, response.summary_data);

        return result;
    });
}

function _fillOrderBy(serverOrderBy: string[]): OrderByInfo[] {
    if (ArrayUtil.isEmptyArray(serverOrderBy))
        return [];
    const result = [];
    for (const singleOrderBy of serverOrderBy) {
        singleOrderBy.trim();
        let field = singleOrderBy;
        let sort = "asc";
        const indexOfSpace = singleOrderBy.indexOf(" ");
        if (indexOfSpace > 0) {
            field = singleOrderBy.substring(0, indexOfSpace);
            sort = singleOrderBy.substring(indexOfSpace + 1).toLowerCase();
        }
        result.push({ field: field, sort: sort });
    }
    return result;
}

export class ModelSearchResult {
    modelRows: ModelRow<any>[];
    orderBy: OrderByInfo[];
    actualRowCount?: number;
    summaryData: ModelRow;

    constructor() {
        this.modelRows = [];
        this.orderBy = [];
        this.actualRowCount = 0;
    }

    getSingleModelRow() {
        return this.modelRows[0];
    }

    mapRowsByField(fieldName: string): Collection<ModelRow> {
        const result = {};
        this.modelRows?.forEach(modelRow => result[modelRow.get(fieldName)] = modelRow);
        return result;
    }

    groupRowsByField(fieldName: string): Collection<ModelRow[]> {
        const result = {};
        this.modelRows?.forEach(modelRow => {
            const key = modelRow.get(fieldName);
            if (!result[key]) {
                result[key] = [];
            }
            result[key].push(modelRow);
        });
        return result;
    }
}

function buildModelRow<RowType>(data: any, modelPath: string, rowConstructor?: RowConstructor, filter?: any): ModelRow<RowType> {
    const row = rowConstructor == null ? new ModelRow<RowType>(modelPath) : new rowConstructor();
    row._appending = false;
    if (filter != null && "lm_search" in filter)
        row.type = ModelRowType.LOOKUP_MODEL_DATA;

    if (rowConstructor == null)
        row.setValues(data);
    else
        row.set(data);

    return row as ModelRow<RowType>;
}
